/**
 * Importación de Conduces x Mayor (consignaciones pasadas) desde otros sistemas.
 *
 * Copia recortada de importarxmayor.js: dos plantillas Excel, "Validar" llama
 * /api/conducexmayor_importar_validar (dry-run en el servidor) y "Importar"
 * llama /api/conducexmayor_importar_guardar, que revalida y graba TODO en una
 * transacción (si algo falla no se importa nada).
 *
 * Solo se importa lo que SIGUE EN PISO: cada conduce entra abierto con todos
 * sus motores sin vender, y de ahí en adelante el flujo normal (marcar
 * vendido, facturar, devolver) sigue en Consignaciones x Mayor. Lo ya vendido
 * y facturado en el sistema origen se importa por "Importar Ventas x Mayor";
 * lo devuelto no hay que importarlo. Por eso aquí no hay cuotas ni pagos.
 */
import React, { useCallback, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import { DataGrid } from "@mui/x-data-grid";
import MDButton from "components/MDButton";
import { FP, PageHeader, fpDataGridSx } from "components/financepro";
import { useApi } from "lib/api";
import { parseExcelPreview, downloadTemplateXlsx } from "modules/importacion/excelPreview";

const PLANTILLAS = {
  conduces: {
    titulo: "Plantilla de conduces",
    descripcion: "Datos generales: una fila por conduce.",
    columnas: [
      "Conduce",
      "Cliente",
      "Rnc",
      "Ubicacion",
      "Telefono",
      "Fecha",
      "Moneda",
      "Tasa",
      "Concepto",
    ],
    archivo: "plantilla_conduces_xmayor.xlsx",
  },
  detalles: {
    titulo: "Plantilla de detalle",
    descripcion: "Detalle por chasis: una fila por motor en piso.",
    columnas: [
      "Conduce",
      "Chasis",
      "Marca",
      "Modelo",
      "Color",
      "Costo",
      "Precio",
      "Precio Min",
    ],
    archivo: "plantilla_detalle_conduce_xmayor.xlsx",
  },
};

// El backend normaliza encabezados sin distinguir mayúsculas/espacios, pero
// aquí se exige la plantilla exacta para atajar archivos equivocados temprano.
function normalizarEncabezado(valor) {
  return String(valor ?? "").trim().toLowerCase().replace(/\s+/g, " ");
}

function encabezadosCoinciden(actuales, esperados) {
  if (!Array.isArray(actuales) || actuales.length !== esperados.length) return false;
  return esperados.every((e, i) => normalizarEncabezado(actuales[i]) === normalizarEncabezado(e));
}

function archivoVacio() {
  return { fileName: null, rows: [], rowCount: 0, error: null, loading: false };
}

const fmtMoneda = new Intl.NumberFormat("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const etiquetaMoneda = (m) => (m === "U" ? "US$" : "RD$");

// El theme de Material Dashboard pone TableHead en display:block (para sus
// tablas propias), lo que despega el encabezado de las filas del cuerpo;
// esto restaura el layout estándar de tabla en las tablas del diálogo.
const tablaHeadSx = { display: "table-header-group", p: 0, borderRadius: 0 };

export default function ImportarConducesXmayor() {
  const { post } = useApi();

  const [archivos, setArchivos] = useState({ conduces: archivoVacio(), detalles: archivoVacio() });
  const [validacion, setValidacion] = useState(null); // respuesta de /validar
  const [validando, setValidando] = useState(false);
  const [importando, setImportando] = useState(false);
  const [errorGeneral, setErrorGeneral] = useState("");
  const [confirmarOpen, setConfirmarOpen] = useState(false);
  const [resultado, setResultado] = useState(null); // respuesta de /guardar
  const [detalle, setDetalle] = useState(null); // conduce seleccionado en la vista previa

  const inputConducesRef = useRef(null);
  const inputDetallesRef = useRef(null);
  const refs = { conduces: inputConducesRef, detalles: inputDetallesRef };

  const handleArchivo = useCallback(async (clave, event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    // Cualquier cambio de archivo invalida la validación y el resultado previos.
    setValidacion(null);
    setDetalle(null);
    setResultado(null);
    setErrorGeneral("");
    setArchivos((prev) => ({ ...prev, [clave]: { ...prev[clave], loading: true, error: null } }));
    try {
      // maxPreviewRows alto: aquí las filas parseadas SON las que se importan.
      const r = await parseExcelPreview(file, { maxPreviewRows: 100000 });
      const okHeaders = encabezadosCoinciden(r.headers, PLANTILLAS[clave].columnas);
      setArchivos((prev) => ({
        ...prev,
        [clave]: {
          fileName: r.fileName,
          rows: okHeaders ? r.rows.map(({ id, ...resto }) => resto) : [],
          rowCount: r.rowCount,
          loading: false,
          error: okHeaders
            ? null
            : `Las columnas no coinciden con la ${PLANTILLAS[clave].titulo.toLowerCase()}. ` +
              `Deben ser exactamente: ${PLANTILLAS[clave].columnas.join(", ")}.`,
        },
      }));
    } catch (err) {
      setArchivos((prev) => ({
        ...prev,
        [clave]: { ...archivoVacio(), error: err?.message || "Error al leer el archivo." },
      }));
    }
  }, []);

  const handleQuitar = useCallback((clave) => {
    setArchivos((prev) => ({ ...prev, [clave]: archivoVacio() }));
    setValidacion(null);
    setDetalle(null);
    setResultado(null);
    setErrorGeneral("");
  }, []);

  const listosParaValidar =
    archivos.conduces.rows.length > 0 &&
    archivos.detalles.rows.length > 0 &&
    !archivos.conduces.error &&
    !archivos.detalles.error;

  const handleValidar = useCallback(async () => {
    setValidando(true);
    setErrorGeneral("");
    setValidacion(null);
    setDetalle(null);
    setResultado(null);
    try {
      const { ok, status, data } = await post("/api/conducexmayor_importar_validar", {
        conduces: archivos.conduces.rows,
        detalles: archivos.detalles.rows,
      });
      if (!ok || data?.Error) {
        // Sin body JSON (404/405 del servidor) no hay Error que mostrar: se
        // agrega el codigo para no dejar un mensaje sin pista de la causa.
        throw new Error(data?.Error || `No se pudo validar la importación (HTTP ${status}).`);
      }
      setValidacion(data);
    } catch (e) {
      setErrorGeneral(e?.message || "No se pudo validar la importación.");
    } finally {
      setValidando(false);
    }
  }, [post, archivos]);

  const handleImportar = useCallback(async () => {
    setConfirmarOpen(false);
    setImportando(true);
    setErrorGeneral("");
    try {
      const { ok, status, data } = await post("/api/conducexmayor_importar_guardar", {
        conduces: archivos.conduces.rows,
        detalles: archivos.detalles.rows,
      });
      if (!ok || data?.Error) {
        const extra = Array.isArray(data?.errores) && data.errores.length
          ? " " + data.errores.join(" | ")
          : "";
        throw new Error((data?.Error || `No se pudo importar (HTTP ${status}).`) + extra);
      }
      setResultado(data);
      setValidacion(null);
      setDetalle(null);
      setArchivos({ conduces: archivoVacio(), detalles: archivoVacio() });
    } catch (e) {
      setErrorGeneral(e?.message || "No se pudo importar.");
    } finally {
      setImportando(false);
    }
  }, [post, archivos]);

  const columnasPrevia = useMemo(
    () => [
      { field: "conduce", headerName: "Conduce origen", width: 130 },
      { field: "cliente", headerName: "Cliente", flex: 1, minWidth: 180 },
      { field: "rnc", headerName: "RNC", width: 120 },
      {
        field: "clienteExistente",
        headerName: "Cliente en sistema",
        width: 140,
        renderCell: (p) =>
          p.value ? (
            <Chip size="small" label={`Existente #${p.value}`} />
          ) : (
            <Chip size="small" color="info" label="Se creará" />
          ),
      },
      { field: "fecha", headerName: "Fecha", width: 110 },
      {
        field: "moneda",
        headerName: "Moneda",
        width: 90,
        renderCell: (p) => (
          <Chip size="small" variant="outlined" label={etiquetaMoneda(p.value)} />
        ),
      },
      {
        field: "chasis",
        headerName: "Motores",
        width: 120,
        renderCell: (p) => {
          const nuevos = (p.value || []).filter((c) => c.nuevo).length;
          return `${(p.value || []).length}${nuevos ? ` (${nuevos} nuevos)` : ""}`;
        },
      },
      {
        field: "total", headerName: "Total", width: 130, align: "right", headerAlign: "right",
        renderCell: (p) => fmtMoneda.format(p.value || 0),
      },
    ],
    []
  );

  const columnasResultado = useMemo(
    () => [
      { field: "conduce", headerName: "Conduce origen", width: 140 },
      { field: "id", headerName: "Conduce interno", width: 140 },
      { field: "cliente", headerName: "Cliente #", width: 110 },
      {
        field: "moneda", headerName: "Moneda", width: 90,
        renderCell: (p) => etiquetaMoneda(p.value),
      },
      { field: "chasis", headerName: "Motores", width: 100, align: "right", headerAlign: "right" },
      {
        field: "total", headerName: "Total", width: 130, align: "right", headerAlign: "right",
        renderCell: (p) => fmtMoneda.format(p.value || 0),
      },
    ],
    []
  );

  const resumen = validacion?.resumen;

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Importar Conduces x Mayor"
        subtitle="Importa las consignaciones pendientes (motores en piso del cliente) desde las dos plantillas Excel"
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {["conduces", "detalles"].map((clave) => (
          <input
            key={clave}
            ref={refs[clave]}
            type="file"
            accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            style={{ display: "none" }}
            onChange={(e) => handleArchivo(clave, e)}
          />
        ))}

        <Alert severity="info" sx={{ mb: 3 }}>
          Solo se importa lo que <b>sigue en piso</b> del cliente: cada conduce entra abierto y el
          marcado de vendidos, la facturación y las devoluciones siguen en Consignaciones x Mayor.
          Lo ya vendido y facturado en el sistema anterior se importa por Importar Ventas x Mayor.
        </Alert>

        {/* Paso 1: archivos */}
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          {["conduces", "detalles"].map((clave) => {
            const plantilla = PLANTILLAS[clave];
            const estado = archivos[clave];
            return (
              <Grid item xs={12} md={6} key={clave}>
                <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, height: "100%" }}>
                  <Typography variant="h6" gutterBottom>
                    {plantilla.titulo}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {plantilla.descripcion} Columnas: {plantilla.columnas.join(", ")}.
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                    <MDButton
                      variant="outlined"
                      color="dark"
                      size="small"
                      startIcon={<DownloadOutlinedIcon />}
                      onClick={() => downloadTemplateXlsx(plantilla.archivo, plantilla.columnas)}
                    >
                      Plantilla
                    </MDButton>
                    <MDButton
                      variant="gradient"
                      color="info"
                      size="small"
                      startIcon={<UploadFileOutlinedIcon />}
                      disabled={estado.loading || importando}
                      onClick={() => refs[clave].current?.click()}
                    >
                      {estado.fileName ? "Cambiar archivo" : "Elegir archivo"}
                    </MDButton>
                    {estado.fileName && (
                      <MDButton variant="outlined" color="dark" size="small" onClick={() => handleQuitar(clave)}>
                        Quitar
                      </MDButton>
                    )}
                    {estado.fileName && <Chip label={estado.fileName} variant="outlined" size="small" />}
                    {estado.rowCount > 0 && !estado.error && (
                      <Chip label={`${estado.rowCount} fila${estado.rowCount === 1 ? "" : "s"}`} color="primary" size="small" />
                    )}
                    {estado.loading && <CircularProgress size={20} />}
                  </Stack>
                  {estado.error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {estado.error}
                    </Alert>
                  )}
                </Paper>
              </Grid>
            );
          })}
        </Grid>

        {/* Paso 2: validar */}
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <MDButton
            variant="gradient"
            color="info"
            startIcon={<FactCheckOutlinedIcon />}
            disabled={!listosParaValidar || validando || importando}
            onClick={handleValidar}
          >
            {validando ? "Validando..." : "Validar archivos"}
          </MDButton>
          {validacion?.valido && (
            <MDButton
              variant="gradient"
              color="success"
              disabled={importando}
              onClick={() => setConfirmarOpen(true)}
            >
              {importando ? "Importando..." : "Importar"}
            </MDButton>
          )}
          {importando && <CircularProgress size={22} />}
        </Stack>

        {errorGeneral && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errorGeneral}
          </Alert>
        )}

        {/* Resultado de la validación */}
        {validacion && (
          <>
            <Alert severity={validacion.valido ? "success" : "error"} sx={{ mb: 2 }}>
              <AlertTitle>
                {validacion.valido
                  ? "Validación correcta: todo listo para importar"
                  : "La importación tiene errores; corrija el Excel y vuelva a cargarlo"}
              </AlertTitle>
              {resumen && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                  <Chip size="small" label={`${resumen.conduces} conduces`} />
                  <Chip size="small" label={`${resumen.chasis} motores (${resumen.chasisNuevos} nuevos)`} />
                  <Chip size="small" label={`${resumen.clientesNuevos} clientes nuevos`} />
                  {resumen.montoTotal > 0 && (
                    <Chip size="small" label={`Total RD$ ${fmtMoneda.format(resumen.montoTotal)}`} />
                  )}
                  {resumen.montoTotalUS > 0 && (
                    <Chip size="small" label={`Total US$ ${fmtMoneda.format(resumen.montoTotalUS)}`} />
                  )}
                </Stack>
              )}
            </Alert>

            {validacion.errores?.length > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <AlertTitle>Errores ({validacion.errores.length})</AlertTitle>
                {validacion.errores.map((e, i) => (
                  <Typography key={`err-${i}`} variant="body2">
                    • {e}
                  </Typography>
                ))}
              </Alert>
            )}

            {validacion.avisos?.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle>Avisos ({validacion.avisos.length})</AlertTitle>
                {validacion.avisos.map((a, i) => (
                  <Typography key={`avi-${i}`} variant="body2">
                    • {a}
                  </Typography>
                ))}
              </Alert>
            )}

            {validacion.conduces?.length > 0 && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Pulse un conduce para ver su detalle completo (cliente y motores) antes de
                  importar.
                </Typography>
                <Paper elevation={0} sx={{ borderRadius: 2, overflow: "hidden", mb: 3 }}>
                  <DataGrid
                    autoHeight
                    rows={validacion.conduces.map((c, i) => ({ ...c, _rid: i }))}
                    getRowId={(r) => r._rid}
                    columns={columnasPrevia}
                    density="compact"
                    disableRowSelectionOnClick
                    onRowClick={(params) => setDetalle(params.row)}
                    sx={{ ...fpDataGridSx, "& .MuiDataGrid-row": { cursor: "pointer" } }}
                    pageSizeOptions={[25, 50, 100]}
                    initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                  />
                </Paper>
              </>
            )}
          </>
        )}

        {/* Resultado de la importación */}
        {resultado && (
          <>
            <Alert severity="success" sx={{ mb: 2 }}>
              <AlertTitle>{resultado.ok}</AlertTitle>
              {resultado.clientesCreados > 0 && (
                <Typography variant="body2">Clientes creados: {resultado.clientesCreados}.</Typography>
              )}
              <Typography variant="body2">
                Cada conduce conserva su número de origen como referencia y recibió un número
                interno nuevo. Ya están abiertos en Consignaciones x Mayor.
              </Typography>
            </Alert>
            {resultado.conduces?.length > 0 && (
              <Paper elevation={0} sx={{ borderRadius: 2, overflow: "hidden", mb: 3 }}>
                <DataGrid
                  autoHeight
                  rows={resultado.conduces.map((c, i) => ({ ...c, _rid: i }))}
                  getRowId={(r) => r._rid}
                  columns={columnasResultado}
                  density="compact"
                  disableRowSelectionOnClick
                  sx={fpDataGridSx}
                  pageSizeOptions={[25, 50, 100]}
                  initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                />
              </Paper>
            )}
          </>
        )}
      </Box>

      {/* Detalle de un conduce de la vista previa: cliente y motores tal cual
          se van a grabar (todos en piso). */}
      <Dialog open={Boolean(detalle)} onClose={() => setDetalle(null)} maxWidth="md" fullWidth>
        {detalle && (
          <>
            <DialogTitle>
              Conduce {detalle.conduce} — {detalle.cliente}
            </DialogTitle>
            <DialogContent dividers>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                <Chip size="small" label={etiquetaMoneda(detalle.moneda)} />
                <Chip size="small" label={`Fecha ${detalle.fecha || "-"}`} />
                <Chip size="small" label={`RNC ${detalle.rnc}`} />
                {detalle.moneda === "U" && <Chip size="small" label={`Tasa ${detalle.tasa}`} />}
                {detalle.clienteExistente ? (
                  <Chip size="small" label={`Cliente existente #${detalle.clienteExistente}`} />
                ) : (
                  <Chip size="small" color="info" label="Cliente se creará" />
                )}
              </Stack>

              {!detalle.clienteExistente && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Se creará el cliente <strong>{detalle.cliente}</strong> con RNC {detalle.rnc}
                  {detalle.ubicacion ? `, dirección "${detalle.ubicacion}"` : ", sin dirección"}
                  {detalle.telefono ? ` y teléfono ${detalle.telefono}.` : " y sin teléfono."}
                </Alert>
              )}

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Total ${etiquetaMoneda(detalle.moneda)} ${fmtMoneda.format(detalle.total || 0)}`}
                />
                <Chip size="small" variant="outlined" label={`${detalle.chasis?.length || 0} motores en piso`} />
              </Stack>

              <Typography variant="subtitle2" gutterBottom>
                Motores ({detalle.chasis?.length || 0})
              </Typography>
              <TableContainer component={Paper} elevation={0} sx={{ mb: 2, border: 1, borderColor: "divider" }}>
                <Table size="small">
                  <TableHead sx={tablaHeadSx}>
                    <TableRow>
                      <TableCell>Chasis</TableCell>
                      <TableCell>Marca</TableCell>
                      <TableCell>Modelo</TableCell>
                      <TableCell>Color</TableCell>
                      <TableCell align="right">Costo</TableCell>
                      <TableCell align="right">Precio</TableCell>
                      <TableCell align="right">Precio Min</TableCell>
                      <TableCell>Inventario</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(detalle.chasis || []).map((c) => (
                      <TableRow key={c.chasis}>
                        <TableCell>{c.chasis}</TableCell>
                        <TableCell>{c.marca || "-"}</TableCell>
                        <TableCell>{c.modelo || "-"}</TableCell>
                        <TableCell>{c.color || "-"}</TableCell>
                        <TableCell align="right">{fmtMoneda.format(c.costo || 0)}</TableCell>
                        <TableCell align="right">{fmtMoneda.format(c.precio || 0)}</TableCell>
                        <TableCell align="right">{fmtMoneda.format(c.precioMin || 0)}</TableCell>
                        <TableCell>
                          {c.nuevo ? (
                            <Chip size="small" color="info" label="Se creará" />
                          ) : (
                            <Chip size="small" label="Existente" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Alert severity="info">
                El conduce entra abierto con todos los motores en piso: sin factura, sin NCF y sin
                deuda. El marcado de vendidos y la facturación siguen en Consignaciones x Mayor.
              </Alert>
            </DialogContent>
            <DialogActions>
              <MDButton variant="outlined" color="dark" onClick={() => setDetalle(null)}>
                Cerrar
              </MDButton>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Confirmación antes de grabar */}
      <Dialog open={confirmarOpen} onClose={() => setConfirmarOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirmar importación</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Se importarán {resumen?.conduces ?? 0} conduces con {resumen?.chasis ?? 0} motores
            {resumen?.clientesNuevos ? ` y se crearán ${resumen.clientesNuevos} clientes` : ""}. La
            operación es todo o nada: si alguna fila falla, no se importa ninguna. ¿Desea continuar?
          </Typography>
        </DialogContent>
        <DialogActions>
          <MDButton variant="outlined" color="dark" onClick={() => setConfirmarOpen(false)}>
            Cancelar
          </MDButton>
          <MDButton variant="gradient" color="success" onClick={handleImportar}>
            Importar
          </MDButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
